import { logErrorURL } from '../constants/api-endpoints';

/**
 * Lightweight, dependency-free client-error reporter.
 *
 * It POSTs to the backend Error Logs endpoint using the native `fetch` API
 * (NOT Angular HttpClient) on purpose:
 *   1. It bypasses the AuthInterceptor, so a failed error-report never triggers
 *      another interceptor `catchError` → infinite loop.
 *   2. `keepalive: true` lets the report survive page-unload / navigation, which
 *      is important for page-load and logout-time errors.
 *
 * All failures are swallowed — capturing an error must never produce another one.
 */
export interface ClientErrorPayload {
    category: 'FRONTEND' | 'BACKEND' | 'API' | 'APPLICATION' | 'LLM' | 'AUTH';
    error: string;
    errorDetail?: string;
    source?: string;
}

// Simple throttle so a runaway error loop can't flood the backend.
const recent = new Map<string, number>();
const THROTTLE_MS = 10_000;

export function captureClientError(payload: ClientErrorPayload): void {
    try {
        // Suppress telemetry when client is offline to prevent futile network storms
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return;
        }
        if (payload.error && (payload.error.includes('HTTP 0') || payload.error.toLowerCase().includes('offline'))) {
            return;
        }

        const key = `${payload.category}|${payload.error}`;
        const now = Date.now();
        const last = recent.get(key);
        if (last && now - last < THROTTLE_MS) {
            return;
        }
        recent.set(key, now);

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const token = safeGet('access_token');
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const sessionToken = safeGet('session_token');
        if (sessionToken) headers['Session-Token'] = sessionToken;

        // Resolve the signed-in user's email from every key the app uses to store it.
        // Different flows populate different keys, so mirror the same fallback chain
        // that RfpService/AuthService rely on — otherwise the backend can't resolve the
        // actor and the row is logged as "system".
        const userEmail =
            safeGet('user_email') || safeGetLocal('user_email') ||
            safeGet('Email') || safeGetLocal('Email');
        if (userEmail) headers['X-User-Email'] = userEmail;

        const body = JSON.stringify({
            category: payload.category,
            error: truncate(payload.error, 500),
            errorDetail: payload.errorDetail,
            source: payload.source ? truncate(payload.source, 200) : undefined,
            userEmail: userEmail || undefined,
        });

        // Fire and forget. Never await, never surface failures.
        void fetch(logErrorURL(), {
            method: 'POST',
            headers,
            body,
            keepalive: true,
        }).catch(() => { /* swallow */ });
    } catch {
        /* swallow — error capture must be side-effect free on failure */
    }
}

function truncate(v: string, max: number): string {
    return v && v.length > max ? v.slice(0, max) : v;
}

function safeGet(key: string): string | null {
    try { return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null; }
    catch { return null; }
}

function safeGetLocal(key: string): string | null {
    try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; }
    catch { return null; }
}
